import { useEffect } from 'react';
import { CircularProgress } from '@mui/material';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import AckLayout from '@/components/layout/ack-layout';
import { FlexCenter } from '@/components/layout/styles';
import { useAuth } from '@/components/providers/authentication-provider';
import { JOURNEY_PROGRESS, ROUTES } from '@/lib/constants';
import { SessionStatus } from '@/lib/types';

export const Route = createFileRoute('/_protected')({
  component: ProtectedRoute,
});

interface LayoutConfig {
  showBackButton: boolean;
  progressValue: number;
}

const LAYOUT_BY_PATH: Record<string, LayoutConfig> = {
  [ROUTES.HOME]: { showBackButton: false, progressValue: JOURNEY_PROGRESS.HOME },
  [ROUTES.PAYMENT_DETAILS]: { showBackButton: true, progressValue: JOURNEY_PROGRESS.PAYMENT_DETAILS },
  [ROUTES.FINAL_DELIVERY]: { showBackButton: true, progressValue: JOURNEY_PROGRESS.FINAL_DELIVERY },
  [ROUTES.COMPLETION]: { showBackButton: false, progressValue: JOURNEY_PROGRESS.COMPLETION },
};

const DEFAULT_LAYOUT: LayoutConfig = { showBackButton: false, progressValue: 0 };

function ProtectedRoute() {
  const { status, jobIds } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isUnauthorized =
    status === SessionStatus.UNAUTHENTICATED ||
    (status === SessionStatus.AUTHENTICATED && (!jobIds || (jobIds && jobIds.length === 0)));

  useEffect(() => {
    if (isUnauthorized) {
      navigate({ to: ROUTES.LINK_EXPIRED, replace: true });
    }
  }, [isUnauthorized, navigate]);

  if (status === SessionStatus.LOADING) {
    return (
      <FlexCenter>
        <CircularProgress />
      </FlexCenter>
    );
  }

  if (isUnauthorized) {
    return null;
  }

  const layout = LAYOUT_BY_PATH[pathname.replace(/\/+$/, '') || ROUTES.HOME] || DEFAULT_LAYOUT;

  return (
    <AckLayout showBackButton={layout.showBackButton} progressValue={layout.progressValue} centered>
      <Outlet />
    </AckLayout>
  );
}
